/**
 * HYPSEYA — Facilitator Onboarding
 * 3-step signup for therapists, facilitators, researchers.
 * Route: /facilitator
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import orbUrl from '../orbAsset';


const wordmarkUrl = '/hypseya-wordmark.png';
const API         = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: 'therapist',    label: 'Therapist',              icon: '◯', desc: 'Licensed psychotherapist or psychiatrist' },
  { id: 'doctor',       label: 'Doctor / Psychiatrist',  icon: '✚', desc: 'Medical doctor, psychiatrist or prescriber' },
  { id: 'facilitator',  label: 'Integration Facilitator', icon: '◎', desc: 'Psychedelic integration or somatic guide' },
  { id: 'researcher',   label: 'Researcher',              icon: '⬡', desc: 'Academic or clinical researcher' },
  { id: 'clinic',       label: 'Clinic / Practice',       icon: '⊕', desc: 'Multi-practitioner clinic or retreat centre' },
];

const USE_CASES = [
  { id: 'with_clients', label: 'With my clients', desc: 'Track ERI, share reports, invite clients' },
  { id: 'research',     label: 'For research',    desc: 'Anonymised longitudinal data access' },
  { id: 'personal',     label: 'My own practice', desc: 'Use HYPSEYA for my own integration work' },
];

const inp = {
  width: '100%', padding: '13px 16px', borderRadius: '12px',
  border: '1.5px solid rgba(184,211,207,0.5)', background: 'rgba(255,255,255,0.88)',
  fontSize: '0.95rem', color: '#333', outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box'
};

export default function FacilitatorOnboarding() {
  const navigate  = useNavigate();
  const [step, setStep]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [facilitator, setFacilitator] = useState(null);

  const [form, setForm] = useState({
    role: '',
    name: '', email: '', practice_name: '', country: '', credentials: '',
    use_cases: [],
    invite_name: '', invite_email: '', invite_note: ''
  });

  const toggleUseCase = (id) => {
    setForm(f => ({
      ...f,
      use_cases: f.use_cases.includes(id)
        ? f.use_cases.filter(x => x !== id)
        : [...f.use_cases, id]
    }));
  };

  const submitProfile = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/facilitators`, {
        name: form.name, email: form.email,
        role: form.role, practice_name: form.practice_name,
        country: form.country, credentials: form.credentials,
        use_cases: form.use_cases
      });
      setFacilitator(res.data);
      // Persist facilitator session so PractitionerPortal can auto-authenticate
      localStorage.setItem('hyp_facilitator', JSON.stringify(res.data));
      setStep(3);
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async () => {
    if (!form.invite_email.trim() || !facilitator) return;
    setLoading(true);
    try {
      await axios.post(`${API}/facilitators/${facilitator.facilitator_id}/invite-client`, {
        email: form.invite_email, name: form.invite_name, note: form.invite_note
      });
      navigate('/practitioner');
    } catch {
      alert('Could not send invite. You can do this later from the Portal.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg,#fafbfc 0%,#f0f7f6 35%,#e8f3f1 60%,#f5f8f7 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '40px 24px 60px'
    }}>
      {/* Brand */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '32px' }}>
        <img src={orbUrl} alt="" style={{ width: 64 }} />
        <img src={wordmarkUrl} alt="HYPSEYA" style={{ width: 140, opacity: 0.85 }} />
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
        {[1,2,3].map(n => (
          <div key={n} style={{
            width: n === step ? 28 : 8, height: 8, borderRadius: 4,
            background: n <= step ? '#1E6B68' : 'rgba(184,211,207,0.4)',
            transition: 'all 0.3s'
          }} />
        ))}
      </div>

      <div style={{ width: '100%', maxWidth: '420px' }}>

        {/* ── STEP 1: Role ── */}
        {step === 1 && (
          <>
            <p style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.3rem', color: '#1E6B68', margin: '0 0 6px', textAlign: 'center' }}>
              I am a…
            </p>
            <p style={{ fontSize: '0.82rem', color: '#9AADAB', textAlign: 'center', margin: '0 0 24px' }}>
              Choose what best describes your practice.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {ROLES.map(r => (
                <button key={r.id} onClick={() => { setForm(f => ({ ...f, role: r.id })); setStep(2); }}
                  style={{
                    padding: '18px 20px', borderRadius: '16px', textAlign: 'left',
                    border: `1.5px solid ${form.role === r.id ? '#1E6B68' : 'rgba(184,211,207,0.4)'}`,
                    background: form.role === r.id ? 'rgba(30,107,104,0.06)' : 'rgba(255,255,255,0.88)',
                    cursor: 'pointer', transition: 'all 0.18s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: '1.4rem' }}>{r.icon}</span>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#1E6B68', fontSize: '0.95rem' }}>{r.label}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#9AADAB' }}>{r.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── STEP 2: Details + Use cases ── */}
        {step === 2 && (
          <>
            <p style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.3rem', color: '#1E6B68', margin: '0 0 6px', textAlign: 'center' }}>
              Your details
            </p>
            <p style={{ fontSize: '0.82rem', color: '#9AADAB', textAlign: 'center', margin: '0 0 24px' }}>
              Tell us a bit about your practice.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              <input style={inp} placeholder="Your full name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <input style={inp} placeholder="Professional email *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <input style={inp} placeholder="Practice or clinic name (optional)" value={form.practice_name} onChange={e => setForm(f => ({ ...f, practice_name: e.target.value }))} />
              <input style={inp} placeholder="Country" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
              <input style={inp} placeholder="Credentials / licence (optional)" value={form.credentials} onChange={e => setForm(f => ({ ...f, credentials: e.target.value }))} />
            </div>

            <p style={{ fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9AADAB', marginBottom: 12 }}>
              How will you use HYPSEYA?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
              {USE_CASES.map(uc => (
                <button key={uc.id} onClick={() => toggleUseCase(uc.id)}
                  style={{
                    padding: '14px 16px', borderRadius: '12px', textAlign: 'left',
                    border: `1.5px solid ${form.use_cases.includes(uc.id) ? '#1E6B68' : 'rgba(184,211,207,0.4)'}`,
                    background: form.use_cases.includes(uc.id) ? 'rgba(30,107,104,0.06)' : 'rgba(255,255,255,0.88)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${form.use_cases.includes(uc.id) ? '#1E6B68' : '#C8D8D6'}`,
                    background: form.use_cases.includes(uc.id) ? '#1E6B68' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {form.use_cases.includes(uc.id) && <span style={{ color: '#fff', fontSize: '0.65rem', fontWeight: '900' }}>✓</span>}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', color: '#1E6B68', fontSize: '0.88rem' }}>{uc.label}</p>
                    <p style={{ margin: '1px 0 0', fontSize: '0.75rem', color: '#9AADAB' }}>{uc.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            <button onClick={submitProfile} disabled={loading || !form.name.trim() || !form.email.trim()}
              style={{
                width: '100%', padding: '15px', borderRadius: '14px', border: 'none',
                background: loading ? '#9AADAB' : '#1E6B68', color: '#fff',
                fontWeight: '600', fontSize: '0.95rem',
                cursor: loading ? 'default' : 'pointer'
              }}>
              {loading ? 'Creating account…' : 'Create my facilitator account →'}
            </button>
          </>
        )}

        {/* ── STEP 3: Invite first client ── */}
        {step === 3 && facilitator && (
          <>
            <p style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.3rem', color: '#1E6B68', margin: '0 0 6px', textAlign: 'center' }}>
              Your account is ready.
            </p>
            <p style={{ fontSize: '0.82rem', color: '#9AADAB', textAlign: 'center', margin: '0 0 24px', lineHeight: '1.6' }}>
              Invite your first client, or skip and go to your portal now.
            </p>

            {/* Access code */}
            <div style={{
              background: 'rgba(30,107,104,0.06)', borderRadius: '16px',
              padding: '18px 20px', marginBottom: '24px',
              border: '1px solid rgba(30,107,104,0.15)', textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 6px', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9AADAB' }}>
                Your client access code
              </p>
              <p style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: '#1E6B68', letterSpacing: '0.15em', fontFamily: 'monospace' }}>
                {facilitator.access_code}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#9AADAB' }}>
                Share this code with clients to connect to your portal.
              </p>
            </div>

            {/* Invite form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              <input style={inp} placeholder="Client's name (optional)" value={form.invite_name} onChange={e => setForm(f => ({ ...f, invite_name: e.target.value }))} />
              <input style={inp} placeholder="Client's email" value={form.invite_email} onChange={e => setForm(f => ({ ...f, invite_email: e.target.value }))} />
              <textarea style={{ ...inp, resize: 'none', lineHeight: '1.6' }} rows={2} placeholder="Personal note (optional)" value={form.invite_note} onChange={e => setForm(f => ({ ...f, invite_note: e.target.value }))} />
            </div>

            <button onClick={sendInvite} disabled={loading || !form.invite_email.trim()}
              style={{
                width: '100%', padding: '14px', borderRadius: '14px', border: 'none',
                background: loading ? '#9AADAB' : '#1E6B68', color: '#fff',
                fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer', marginBottom: 10
              }}>
              {loading ? 'Sending…' : 'Send invitation →'}
            </button>
            <button onClick={() => navigate('/practitioner')}
              style={{
                width: '100%', padding: '14px', borderRadius: '14px',
                border: '1px solid rgba(184,211,207,0.5)', background: 'none',
                color: '#9AADAB', fontSize: '0.9rem', cursor: 'pointer'
              }}>
              Skip — go to my portal
            </button>
          </>
        )}
      </div>
    </div>
  );
}
